/**
 * src/csv/exportCsv.ts — manual CSV generators (no dependency, §3, §10.10).
 */
import type { ProjectionResult, ProjectionSummary } from '@/types';

/** Quote a CSV field if it contains a comma, quote, or newline. */
function csvField(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(headers: string[], rows: (string | number)[][]): string {
  const lines = [headers.map(csvField).join(',')];
  for (const row of rows) lines.push(row.map(csvField).join(','));
  return lines.join('\n');
}

const MONTH_COLUMNS: (keyof ProjectionResult['rows'][number])[] = [
  'monthIndex', 'date', 'baseRate', 'apr', 'afterTaxApr', 'drawThisMonth', 'paymentThisMonth',
  'interestThisMonth', 'startBalance', 'endBalance', 'portfolioValue', 'basis', 'gainFraction',
  'deployedProceeds', 'netWorth', 'loanValueOfCollateral', 'ltv', 'marginOfSafety', 'callCushionPct',
  'maintenanceCall', 'forcedSale', 'realizedTaxThisMonth', 'cumulativeInterest', 'cumulativeDraws',
  'cumulativePayments', 'cumulativeTax',
];

/** Full month-by-month series for a single scenario (§10.10). */
export function monthlyRowsToCsv(result: ProjectionResult): string {
  const rows = result.rows.map((r) => MONTH_COLUMNS.map((c) => (typeof r[c] === 'boolean' ? (r[c] ? 1 : 0) : (r[c] as string | number))));
  return toCsv(MONTH_COLUMNS as string[], rows);
}

export interface ComparisonRow {
  name: string;
  summary: ProjectionSummary;
  estateDifference: number;
}

/** Side-by-side comparison CSV (§10.5, §10.10). */
export function comparisonToCsv(items: ComparisonRow[]): string {
  const headers = [
    'scenario', 'startApr', 'effectiveSpread', 'endingNetWorth', 'totalInterest',
    'worstLtv', 'minCallCushionPct', 'firstMaintenanceCallMonth', 'estateDifference',
  ];
  const rows = items.map((i) => [
    i.name,
    i.summary.startApr,
    i.summary.effectiveSpread,
    i.summary.endingNetWorth,
    i.summary.totalInterest,
    i.summary.worstLtv,
    i.summary.minCallCushionPct,
    i.summary.firstMaintenanceCallMonth ?? '',
    i.estateDifference,
  ]);
  return toCsv(headers, rows);
}
